import React, { useState, useEffect, useRef } from "react";
import styles from "./Contact.module.css";
import testimonialImage from "../../assets/images/testimonial-image.jpg";
import { FaStar, FaQuoteLeft, FaQuoteRight } from "react-icons/fa";
import Alert from "@mui/material/Alert";

const API_URL = process.env.REACT_APP_API_URL_CONTACT;
const SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY; // v2 checkbox

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // NEW: explicit reCAPTCHA render (fixes SPA navigation issue)
  const recaptchaRef = useRef(null);
  const [widgetId, setWidgetId] = useState(null);

  // Testimonials (single-card carousel)
  const testimonials = [
    {
      text:
        "iCredentialing helped us reduce our credentialing timeline from 120 days to just 45 days! It's been a game-changer for our practice growth.",
      author: "John",
      role: "Healthcare Provider",
      image: testimonialImage,
      stars: 5,
    },
    {
      text:
        "The iCredentialing team delivered accurate and compliant credentialing and payer enrollment services, which reduced the burden on my practice and staff, increased efficiency, and allowed us to focus more on patient care.",
      author: "Dr. Mazharul Huq",
      role: "Physician",
      image: null,
      stars: 5,
    },
    {
      text:
        "iCredentialing’s Account Manager provided exceptional credentialing support. They were professional, highly responsive, and remarkably efficient. Their clear communication and attention to detail saved me a great deal of time and stress. I would confidently recommend iCredentialing to any physician or practice.",
      author: "Dr. Zuhra Musherraf",
      role: "Physician",
      image: null,
      stars: 5,
    },
  ];

  const [testimonialIndex, setTestimonialIndex] = useState(1); // starts on John
  const t = testimonials[testimonialIndex];

  const handlePrevTestimonial = () => {
    setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length);
  };

  const handleNextTestimonial = () => {
    setTestimonialIndex((i) => (i + 1) % testimonials.length);
  };

  useEffect(() => {
    let cancelled = false;

    const ensureScript = () =>
      new Promise((resolve) => {
        if (window.grecaptcha?.render) return resolve();
        const existing = document.querySelector(
          'script[data-recaptcha="v2-explicit"]'
        );
        if (existing) {
          const check = () =>
            window.grecaptcha?.render ? resolve() : setTimeout(check, 50);
          check();
          return;
        }
        const s = document.createElement("script");
        s.src = "https://www.google.com/recaptcha/api.js?render=explicit";
        s.async = true;
        s.defer = true;
        s.dataset.recaptcha = "v2-explicit";
        s.onload = resolve;
        document.head.appendChild(s);
      });

    const renderWidget = async () => {
      await ensureScript();
      if (cancelled || !recaptchaRef.current) return;
      // clear stale iframe when navigating back
      recaptchaRef.current.innerHTML = "";
      const id = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: SITE_KEY_V2,
        theme: "light",
        size: "normal",
      });
      if (!cancelled) setWidgetId(id);
    };

    renderWidget();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(false);
    setSubmitError("");
    if (submitting) return;

    // NEW: read token from this widget instance
    const token =
      widgetId != null ? window.grecaptcha.getResponse(widgetId) : "";
    if (!token) {
      setSubmitError("Please check the reCAPTCHA box before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          pageUri: window.location.href,
          pageName: document.title,
          captchaToken: token,
          captchaProvider: "v2_checkbox",
        }),
      });

      // safer success check: rely on HTTP status; body may be json/text/empty
      let payload;
      const ct = resp.headers.get("content-type") || "";
      try {
        payload = ct.includes("application/json")
          ? await resp.json()
          : await resp.text();
      } catch {
        payload = null;
      }
      if (!resp.ok) {
        const detail =
          typeof payload === "string" ? payload : JSON.stringify(payload);
        throw new Error(
          `HTTP ${resp.status} ${resp.statusText}${
            detail ? ` — ${detail}` : ""
          }`
        );
      }

      setSubmitError("");
      setSubmitted(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        company: "",
        message: "",
      });

      // NEW: reset just this widget
      if (widgetId != null) window.grecaptcha.reset(widgetId);
    } catch (err) {
      console.error("Error submitting form:", err);
      setSubmitted(false);
      setSubmitError(
        "There was an error submitting your form. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles["contact-section"]}>
      <div className={styles.container}>
        <div className={styles["contact-testimonial-container"]}>
          <div className={styles["contact-column"]}>
            <header className={styles["contact-header"]}>
              <h2 className={styles["question-title"]}>
                HAVE A QUESTION? GET IN TOUCH.
              </h2>
            </header>

            <h2 className={styles["contact-title"]}>Contact Us</h2>
            <p className={styles["support-text"]}>
              Learn how we can support your organization and its revenue goals.
            </p>

            {(submitted || submitError) && (
              <div style={{ margin: "16px 0 24px" }}>
                {submitted && (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Thank you for contacting us! Our team will reach out
                    shortly.
                  </Alert>
                )}
                {submitError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {submitError}
                  </Alert>
                )}
              </div>
            )}

            <div className={styles["contact-form-container"]}>
              <form className={styles["contact-form"]} onSubmit={handleSubmit}>
                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="company">Company Name *</label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className={styles["form-row"]}>
                  <div className={styles["input-wrapper"]}>
                    <label htmlFor="message">Your Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                    />
                  </div>
                </div>

                {/* reCAPTCHA checkbox (explicit render) */}
                <div style={{ margin: "10px 0" }}>
                  <div ref={recaptchaRef} />
                </div>

                <button
                  type="submit"
                  className={styles["submit-button"]}
                  disabled={submitting}
                >
                  <strong>
                    {submitting ? "Submitting..." : "Submit Request"}
                  </strong>
                </button>
              </form>
            </div>
          </div>

          {/* testimonial column */}
          <aside className={styles["testimonials-column"]}>
            <header className={styles["reviews-header"]}>
              <h2 className={styles["reviews-title"]}>CUSTOMER REVIEWS</h2>
              <hr className={styles["question-divider"]} />
              <hr className={styles["reviews-underline"]} />
            </header>
            <h2 className={styles["testimonials-title"]}>Testimonials</h2>
            <p className={styles["testimonials-subtitle"]}>
              Hear what our customers have to say about us
            </p>
            <div className={styles["testimonial-container"]}>
              <div className={`${styles["blue-bar"]} ${styles["left"]}`} aria-hidden="true"></div>
                <article className={styles["testimonial-card"]}>
                  <div className={styles.stars} aria-label={`${t.stars} out of 5 stars`}>
                    {[...Array(t.stars)].map((_, i) => (
                      <div key={i} className={styles.starBox}>
                        <FaStar className={styles["star-icon"]} aria-hidden="true" />
                      </div>
                    ))}
                  </div>

                  <div className={styles["content"]}>
                    <blockquote className={styles["testimonial-content"]}>
                      <FaQuoteLeft className={styles["quote-open"]} aria-hidden="true" />
                      <p className={styles["testimonial-text"]}>{t.text}</p>
                      <FaQuoteRight className={styles["quote-close"]} aria-hidden="true" />
                    </blockquote>

                    <footer className={styles["testimonial-author"]}>
                      {t.image ? (
                        <figure className={styles["image-crop"]}>
                          <img
                            src={t.image}
                            alt={t.author}
                            className={styles["author-image"]}
                          />
                        </figure>
                      ) : null}

                      <div className={styles["author-info"]}>
                        <p className={styles["author-name"]}>{t.author}</p>
                        <p className={styles["author-name"]}>{t.role}</p>
                      </div>
                    </footer>
                  </div>

                  <nav className={styles["testimonial-nav"]} aria-label="Testimonial navigation">
                    <button
                      type="button"
                      className={styles["prev"]}
                      aria-label="Previous testimonial"
                      onClick={handlePrevTestimonial}
                    >
                      &laquo;
                    </button>
                    <button
                      type="button"
                      className={styles["next"]}
                      aria-label="Next testimonial"
                      onClick={handleNextTestimonial}
                    >
                      &raquo;
                    </button>
                  </nav>
                </article>
              <div className={`${styles["blue-bar"]} ${styles["right"]}`} aria-hidden="true"></div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
